import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../utils/api';
import { Button, Badge, Alert, SearchBar, Spinner, ScopeBadges } from './shared';
import SyncPickerModal from './SyncPickerModal';

const STATUS_BADGE = { active: 'success', inactive: 'warning', deleted: 'danger' };

const formatTokens = (count) => {
    if (!count) return null;
    if (count >= 1000) return (count / 1000).toFixed(1) + 'k';
    return String(count);
};

export default function AdminKnowledge() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [syncPickerOpen, setSyncPickerOpen] = useState(false);

    useEffect(() => { loadItems(); }, []);

    const loadItems = async () => {
        try {
            setLoading(true);
            const res = await adminApi.listKnowledge({ include_inactive: true });
            setItems(res.knowledge_files || []);
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to load knowledge files');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (e, id) => {
        e.stopPropagation();
        if (!confirm(`Delete knowledge "${id}"? This cannot be undone.`)) return;
        try {
            await adminApi.deleteKnowledge(id, true);
            setSuccessMessage(`Knowledge "${id}" deleted`);
            loadItems();
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            setError(err.message);
        }
    };

    const filteredItems = items.filter(k => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return k.id.toLowerCase().includes(q)
            || (k.name || '').toLowerCase().includes(q)
            || (k.description && k.description.toLowerCase().includes(q));
    });

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900">Knowledge Files</h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        {items.length} knowledge file{items.length !== 1 ? 's' : ''} — domain-specific context for agents
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => setSyncPickerOpen(true)}>Sync from Git</Button>
                    <Button size="sm" onClick={() => navigate('/admin/knowledge/new')}>Create Knowledge</Button>
                </div>
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}
            {successMessage && <Alert variant="success" onClose={() => setSuccessMessage(null)} className="mb-4">{successMessage}</Alert>}

            <SearchBar value={searchQuery} onChange={setSearchQuery} onSearch={loadItems} onRefresh={loadItems} placeholder="Search knowledge files..." className="mb-4" />

            {loading ? (
                <Spinner size="lg" text="Loading knowledge files..." center />
            ) : filteredItems.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-sm">No knowledge files found</div>
            ) : (
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50/60">
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Name</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">ID</th>
                                <th className="text-right px-3 py-2.5 text-xs font-medium text-gray-500">Tokens</th>
                                <th className="text-center px-3 py-2.5 text-xs font-medium text-gray-500">Status</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Source</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Tags</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Scope</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 w-40">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filteredItems.map((k) => (
                                <tr key={k.id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => navigate(`/admin/knowledge/${k.id}`)}>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            <Badge variant="outline">{k.name}</Badge>
                                        </div>
                                        {k.description && <p className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">{k.description}</p>}
                                    </td>
                                    <td className="px-3 py-3">
                                        <span className="text-xs text-gray-500 font-mono">{k.id}</span>
                                    </td>
                                    <td className="px-3 py-3 text-right">
                                        <span className="text-xs text-gray-500 font-mono">{formatTokens(k.token_count) || '-'}</span>
                                    </td>
                                    <td className="px-3 py-3 text-center">
                                        <Badge variant={STATUS_BADGE[k.status] || 'default'} dot>
                                            {k.status?.charAt(0).toUpperCase() + k.status?.slice(1)}
                                        </Badge>
                                    </td>
                                    <td className="px-3 py-3">
                                        <Badge variant={k.is_builtin ? 'success' : 'default'}>{k.is_builtin ? 'Built-in' : 'Custom'}</Badge>
                                    </td>
                                    <td className="px-3 py-3">
                                        {(k.tags || []).length > 0 ? (
                                            <div className="flex flex-wrap gap-1">
                                                {(k.tags || []).slice(0, 4).map((t) => (
                                                    <Badge key={t} variant="info">#{t}</Badge>
                                                ))}
                                                {k.tags.length > 4 && <Badge variant="outline">+{k.tags.length - 4}</Badge>}
                                            </div>
                                        ) : (
                                            <span className="text-gray-300">-</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-3">
                                        <ScopeBadges environment={k.environment} />
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                                            <Button variant="ghost" size="xs" onClick={() => navigate(`/admin/knowledge/${k.id}/edit`)}>Edit</Button>
                                            <Button variant="ghost" size="xs" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={(e) => handleDelete(e, k.id)}>Delete</Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <SyncPickerModal
                open={syncPickerOpen}
                resourceType="knowledge"
                onClose={() => { setSyncPickerOpen(false); loadItems(); }}
                onSuccess={(msg) => {
                    setSuccessMessage(msg);
                    loadItems();
                    setTimeout(() => setSuccessMessage(null), 6000);
                }}
            />
        </div>
    );
}
